import React from 'react';
import { type TomeCorner, TomeVineCorner } from './TomeOrnaments';

/*
 * Frame pieces that break the rectangle (lane N): roots, a branch and vine
 * trails that overhang a tome's edge, plus the vine corners. Inline SVG, static,
 * decorative (aria-hidden, no pointer events). Each piece casts a small
 * shadow so it reads as lying on the cover, not printed on it. The shadow is an
 * SVG filter inside the piece, so it is rasterised with the piece once; a CSS
 * `filter` on the <svg> would be applied by the compositor on every frame,
 * because pieces that overlap the scrolling page become their own layers.
 */

/** In-SVG drop shadow: wrap a piece's art in it. */
const Shadowed: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const id = `tome-shadow-${React.useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  return <>
    <defs><filter id={id} x="-20%" y="-20%" width="140%" height="160%">
      <feDropShadow dx="0" dy="1.6" stdDeviation="1.2" floodColor="#000" floodOpacity="0.42" />
    </filter></defs>
    <g filter={`url(#${id})`}>{children}</g>
  </>;
};

export type TomeSide = 'top' | 'right' | 'bottom' | 'left';
export type TomeFramePieceKind = 'vine' | 'roots' | 'trail' | 'branch';

export interface TomeFramePiece {
  /** `vine` and `roots` grip a corner; `trail` and `branch` lie along a side. */
  piece: TomeFramePieceKind;
  at: TomeCorner | TomeSide;
  /** Side pieces: centre position along the side, 0..1 from its top or left end. Default 0.5. */
  along?: number;
  /** Size in px: a corner piece's square, a side piece's length. */
  size?: number;
  /** Lie over the page instead of growing from behind it (vines always lie over). Costs a layer when it overlaps the scrolling body. */
  over?: boolean;
}

/** Named arrangements. `grove` keeps the original two vine corners and adds roots, a branch and a trail. */
export type TomeFramePreset = 'grove' | 'roots' | 'vines' | 'bare';

export const TOME_FRAME_PRESETS: Record<TomeFramePreset, TomeFramePiece[]> = {
  grove: [
    { piece: 'vine', at: 'top-left' },
    { piece: 'branch', at: 'top', along: 0.42 },
    { piece: 'trail', at: 'left', along: 0.58 },
    { piece: 'roots', at: 'bottom-left' },
    { piece: 'vine', at: 'bottom-right' },
  ],
  roots: [
    { piece: 'vine', at: 'top-left' },
    { piece: 'roots', at: 'bottom-left' },
    { piece: 'roots', at: 'bottom-right' },
    { piece: 'trail', at: 'right', along: 0.5 },
  ],
  vines: [{ piece: 'vine', at: 'top-left' }, { piece: 'vine', at: 'bottom-right' }],
  bare: [],
};

const flip = (corner: TomeCorner, baseRight: boolean, baseBottom: boolean) =>
  `scale(${corner.endsWith('right') === baseRight ? 1 : -1}, ${corner.startsWith('bottom') === baseBottom ? 1 : -1})`;

/** Leaf shape shared by the trail and branch (same leaf as the vine corners). */
const Leaf: React.FC<{ x: number; y: number; rotate: number; scale: number; fill: string; vein?: string }> = ({ x, y, rotate, scale, fill, vein = '#8cd27b' }) =>
  <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`}>
    <path d="M 0 0 C -6 -12, 10 -14, 13 -3 C 10 6, -3 5, 0 0 Z" fill={fill} stroke="#10240f" strokeWidth="0.7" />
    <path d="M 0 0 C -4 -8, 7 -9, 9 -2" stroke={vein} strokeWidth="0.9" />
  </g>;

/** Gnarled roots gripping a corner from outside the page; drawn for bottom-left and mirrored. */
export const TomeRootCorner: React.FC<{ corner: TomeCorner; className?: string; style?: React.CSSProperties; over?: boolean }> = ({ corner, className = '', style, over }) =>
  <svg className={`tome-roots ${className}`} data-corner={corner} data-over={over || undefined} viewBox="0 0 120 120" fill="none" aria-hidden="true"
    style={{ transform: flip(corner, false, true), ...style }}>
    <Shadowed>
      <g stroke="#1c1108" strokeLinecap="round">
        <path d="M18 96 C11 103 6 109 0 119" strokeWidth="4.6" />
        <path d="M42 104 C47 110 53 115 61 120" strokeWidth="3.2" />
        <path d="M12 68 C5 70 1 75 -4 82" strokeWidth="2.8" />
        <path d="M80 104 C86 108 90 113 92 119" strokeWidth="2" />
      </g>
      {/* The main root, tapering from the left edge round the corner and along the foot. */}
      <path d="M0 34 C10 50 15 72 21 91 C25 100 37 103 61 102 C81 101 101 105 120 107 L120 110 C101 109 81 106 61 108 C34 109 17 106 12 96 C6 79 3 56 -3 39 Z"
        fill="#4a301a" stroke="#1c1108" strokeWidth="1.3" strokeLinejoin="round" />
      <g stroke="#8d6640" strokeLinecap="round" fill="none">
        <path d="M2 40 C9 54 12 72 17 89" strokeWidth="1.6" />
        <path d="M26 101 C40 103.5 52 103 62 103.4 C80 102.6 98 105.6 116 107.6" strokeWidth="1.2" />
      </g>
      <g stroke="#2a190b" strokeLinecap="round" strokeWidth="0.9">
        <path d="M8 58 l3 4" /><path d="M13 76 l2.5 3" /><path d="M48 104 l4 0.6" /><path d="M86 105.4 l4 0.8" />
      </g>
      <ellipse cx="15" cy="84" rx="2.6" ry="1.8" fill="#1c1108" transform="rotate(-70 15 84)" />
      <ellipse cx="70" cy="105" rx="2.4" ry="1.4" fill="#1c1108" />
      {/* Moss where the roots meet the corner, and two small toadstools. */}
      <g fill="#3f6b33" stroke="#1d3a19" strokeWidth="0.6">
        <circle cx="20" cy="94" r="4.6" /><circle cx="27" cy="98" r="3.6" /><circle cx="14" cy="99" r="3.2" /><circle cx="33" cy="101" r="2.6" />
      </g>
      <g fill="#86b465"><circle cx="18.6" cy="92.4" r="1.6" /><circle cx="26.6" cy="96.6" r="1.2" /><circle cx="13.6" cy="97.8" r="1" /></g>
      <g>
        <path d="M39 100 v-6" stroke="#efe0bf" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M33.5 94.5 C34 88.5 44 88.5 44.5 94.5 Z" fill="#b5462c" stroke="#5a1d10" strokeWidth="0.8" />
        <circle cx="37" cy="92" r="0.9" fill="#f3e3c3" /><circle cx="41" cy="91.4" r="0.7" fill="#f3e3c3" />
        <path d="M46.5 101 v-3.5" stroke="#efe0bf" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M43.2 97.8 C43.6 94 49.4 94 49.8 97.8 Z" fill="#c4683a" stroke="#5a1d10" strokeWidth="0.7" />
      </g>
    </Shadowed>
  </svg>;

/** A leafy vine lying along a side; drawn horizontal, rotated by placement. */
export const TomeVineTrail: React.FC<{ className?: string; style?: React.CSSProperties; over?: boolean }> = ({ className = '', style, over }) =>
  <svg className={`tome-trail ${className}`} data-over={over || undefined} viewBox="0 0 200 36" fill="none" aria-hidden="true" style={style}>
    <Shadowed>
      <path d="M2 20 C26 10 44 28 70 18 S114 8 134 19 S174 27 196 14" stroke="#10240f" strokeWidth="4" strokeLinecap="round" />
      <path d="M2 20 C26 10 44 28 70 18 S114 8 134 19 S174 27 196 14" stroke="#4f8a45" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M188 16 C196 6 203 12 198 18 C194 21 191 17 194 14" stroke="#6ca864" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M58 21 C56 28 60 31 64 30" stroke="#6ca864" strokeWidth="1" strokeLinecap="round" />
      <Leaf x={26} y={14} rotate={-35} scale={0.95} fill="#2f6233" />
      <Leaf x={48} y={22} rotate={150} scale={0.85} fill="#3f7a3a" />
      <Leaf x={86} y={14} rotate={-20} scale={0.9} fill="#4f8c45" />
      <Leaf x={112} y={13} rotate={165} scale={0.75} fill="#2f6233" />
      <Leaf x={146} y={22} rotate={-45} scale={0.85} fill="#3f7a3a" />
      <Leaf x={172} y={22} rotate={145} scale={0.7} fill="#4f8c45" />
      <circle cx="100" cy="11" r="2.4" fill="#f0d18a" stroke="#7a5410" strokeWidth="0.6" />
      <circle cx="160" cy="26" r="1.9" fill="#e08a5f" stroke="#6b2a14" strokeWidth="0.5" />
    </Shadowed>
  </svg>;

/** A bark branch with a fork, knots, a wrapping tendril and leaves, lying along a side; drawn horizontal. */
export const TomeBranch: React.FC<{ className?: string; style?: React.CSSProperties; over?: boolean }> = ({ className = '', style, over }) =>
  <svg className={`tome-branch ${className}`} data-over={over || undefined} viewBox="0 0 220 44" fill="none" aria-hidden="true" style={style}>
    <Shadowed>
      <path d="M74 21 C82 13 92 8 104 6" stroke="#1c1108" strokeWidth="4.4" strokeLinecap="round" />
      <path d="M74 20.4 C82 12.6 92 7.6 103 5.8" stroke="#7a5432" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M2 25 C30 19 70 21 110 20 C150 19 190 16 218 18 L217 22.5 C190 21.5 150 25 110 26.5 C70 27.5 30 29.5 3 31 Z"
        fill="#4e331d" stroke="#1c1108" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M8 23.6 C40 20 80 21.6 110 21.3 C150 20.6 185 18 213 19" stroke="#916a43" strokeWidth="1.5" strokeLinecap="round" />
      <g stroke="#2a190b" strokeLinecap="round" strokeWidth="0.9">
        <path d="M22 26.5 l6 -0.6" /><path d="M58 25.4 l7 -0.3" /><path d="M128 23.6 l6 -0.4" /><path d="M170 21.6 l5 -0.3" />
      </g>
      <ellipse cx="46" cy="25" rx="3.4" ry="2" fill="#1c1108" />
      <ellipse cx="46" cy="25" rx="1.6" ry="0.9" fill="#6e4c2c" />
      <ellipse cx="146" cy="22.6" rx="2.6" ry="1.6" fill="#1c1108" />
      <path d="M160 21 l2 -6 l3 1 l-1 5.4 z" fill="#5b3c22" stroke="#1c1108" strokeWidth="0.8" />
      <path d="M161.4 15.4 l2.6 0.8" stroke="#d2b07e" strokeWidth="1" />
      <path d="M24 33 C28 16 36 16 40 28 C44 37 50 35 54 22 C56 16 60 14 64 18" stroke="#2f6233" strokeWidth="1.3" strokeLinecap="round" />
      <Leaf x={104} y={6} rotate={-20} scale={0.95} fill="#3f7a3a" />
      <Leaf x={96} y={9} rotate={-150} scale={0.75} fill="#2f6233" />
      <Leaf x={64} y={18} rotate={-60} scale={0.6} fill="#4f8c45" />
      <Leaf x={206} y={18} rotate={20} scale={0.8} fill="#3f7a3a" />
      <Leaf x={196} y={22} rotate={120} scale={0.65} fill="#2f6233" />
    </Shadowed>
  </svg>;

const SIDE_LENGTH: Record<'trail' | 'branch', number> = { trail: 190, branch: 210 };
const SIDE_THICKNESS: Record<'trail' | 'branch', number> = { trail: 36 / 200, branch: 44 / 220 };

function sideStyle(at: TomeSide, along: number, length: number, thickness: number): React.CSSProperties {
  const pct = `${Math.min(1, Math.max(0, along)) * 100}%`;
  const rotate = at === 'left' ? -90 : at === 'right' ? 90 : at === 'bottom' ? 180 : 0;
  return {
    width: length, height: length * thickness,
    left: at === 'left' ? 0 : at === 'right' ? '100%' : pct,
    top: at === 'top' ? 0 : at === 'bottom' ? '100%' : pct,
    transform: `translate(-50%, -50%) rotate(${rotate}deg)`,
  };
}

/** One placed frame piece. Corner pieces use `[data-corner]` CSS; side pieces centre on their side. */
export const TomeFramePieceView: React.FC<TomeFramePiece> = ({ piece, at, along = 0.5, size, over }) => {
  const isCorner = at.includes('-');
  if (piece === 'vine' || piece === 'roots') {
    if (!isCorner) return null;
    const style = size ? { width: size, height: size } : undefined;
    return piece === 'vine'
      ? <TomeVineCorner corner={at as TomeCorner} className="tome-frame-piece" />
      : <TomeRootCorner corner={at as TomeCorner} className="tome-frame-piece" style={style} over={over} />;
  }
  if (isCorner) return null;
  const style = sideStyle(at as TomeSide, along, size ?? SIDE_LENGTH[piece], SIDE_THICKNESS[piece]);
  const Piece = piece === 'trail' ? TomeVineTrail : TomeBranch;
  return <Piece className="tome-frame-piece" style={{ ...style, position: 'absolute' }} over={over} />;
};

/** A tome's frame: a preset name or an explicit list of pieces. Render inside a positioned host. */
export const TomeFrame: React.FC<{ frame: TomeFramePreset | TomeFramePiece[] }> = ({ frame }) => {
  const pieces = typeof frame === 'string' ? TOME_FRAME_PRESETS[frame] : frame;
  return <>{pieces.map((piece, index) => <TomeFramePieceView key={`${piece.piece}-${piece.at}-${index}`} {...piece} />)}</>;
};
